import { deferred } from '../deferred';
import { tick } from '../tick';

/**
 * Wraps a batched async operation, providing an interface which allows individual calls.
 */
export class Batcher<In, Out> {
  /**
   * @param resolver — an async function mapping from an array of the 'In' type to an array of the 'Out' type.
   *    This function must ALWAYS return an array of the same length as its input. Doing otherwise will cause runtime errors.
   */
  constructor(private resolver: (arg: In[]) => PromiseLike<Out[]>) {}

  session?: BatcherSession<In, Out>;

  private createSession = () => {
    const session = new BatcherSession<In, Out>();
    this.session = session;

    void tick().then(async () => {
      this.session = undefined;

      try {
        session.result.resolve(await this.resolver(session.args));
      } catch (error) {
        session.result.reject(error);
      }
    });

    return session;
  };

  public call = async (arg: In): Promise<Out> => {
    const session = this.session ?? this.createSession();

    const index = session.args.length;
    session.args.push(arg);
    const outputs = await session.result.promise;

    /** We permit this unsafe coercion because it will always be sound if the constructor's invariant is met —
     * if the resolver function returns an array of the same length as its input.
     */
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return (outputs[index] as unknown) as Out;
  };
}

class BatcherSession<In, Out> {
  args: In[] = [];
  result = deferred<Out[]>();
}
